/**
 * Manifest diff (pure — unit tested). The manifest is authoritative: given what is stored on the
 * device and a fresh manifest, decide what to delete, what each pack still needs and its status.
 *
 *  - a stored item is valid only when the manifest lists that file at that rev with that part and
 *    the same etag + size; anything else (file deleted, audience narrowed, section changed, new
 *    revision) is deleted from the device.
 *  - a pack whose song/collection is gone from the manifest is removed with its items.
 *  - a valid item no pack plans any more (e.g. "allar raddir" switched off, file moved to another
 *    song) is deleted too.
 *  - the recent-songs LRU keeps only valid entries that are not also stored in a pack.
 */
import type { MediaPart } from '../../shared/dto/library';
import { mediaUrl, type ManifestDTO } from '../../shared/dto/offline';
import type { ItemRow, PackRow, PackStatus, PlannedItem, RecentEntry } from './types';

/** Index of every downloadable part in the manifest: url → { size, etag }. */
export function manifestIndex(manifest: ManifestDTO): Map<string, { size: number; etag: string; fileId: string; songId: string }> {
  const idx = new Map<string, { size: number; etag: string; fileId: string; songId: string }>();
  for (const f of manifest.files) {
    for (const [part, p] of Object.entries(f.parts)) {
      if (!p) continue;
      idx.set(mediaUrl(f.id, f.rev, part as MediaPart), { size: p.size, etag: p.etag, fileId: f.id, songId: f.songId });
    }
  }
  return idx;
}

export function isItemValid(it: Pick<ItemRow, 'url' | 'size' | 'etag'>, idx: ReturnType<typeof manifestIndex>): boolean {
  const m = idx.get(it.url);
  return !!m && m.size === it.size && m.etag === it.etag;
}

export interface PackOutcome {
  status: PackStatus;
  error: string | null;
  bytes: number;
  missing: PlannedItem[];
}

export interface DiffResult {
  /** URLs to delete from the content cache (and the items table). */
  deleteUrls: string[];
  /** Packs to remove (target gone). */
  removePacks: string[];
  /** Kept items whose packIds changed → new packIds. */
  packIdUpdates: Map<string, string[]>;
  /** Per kept pack: new status, planned bytes and what is still missing. */
  packs: Map<string, PackOutcome>;
  /** Recent LRU after pruning, and the URLs to delete from the recent cache. */
  recent: RecentEntry[];
  recentDeleteUrls: string[];
}

/** Status of a pack after re-planning (missing work keeps running automatically). */
export function nextPackStatus(prev: PackStatus, missing: number): PackStatus {
  if (missing === 0) return 'complete';
  return prev === 'complete' || prev === 'update' ? 'update' : 'pending';
}

export function diffManifest(input: {
  manifest: ManifestDTO;
  items: readonly ItemRow[];
  packs: readonly PackRow[];
  /** planPack() result per pack id with the NEW manifest (null = target gone). */
  plans: ReadonlyMap<string, PlannedItem[] | null>;
  recent: readonly RecentEntry[];
}): DiffResult {
  const idx = manifestIndex(input.manifest);
  const deleteUrls: string[] = [];
  const removePacks: string[] = [];
  const packIdUpdates = new Map<string, string[]>();
  const packs = new Map<string, PackOutcome>();

  const valid = new Map<string, ItemRow>();
  for (const it of input.items) {
    if (isItemValid(it, idx)) valid.set(it.url, it);
    else deleteUrls.push(it.url);
  }

  const wanted = new Map<string, string[]>(); // url → pack ids that plan it
  for (const p of input.packs) {
    const plan = input.plans.get(p.id);
    if (!plan) {
      removePacks.push(p.id);
      continue;
    }
    const missing: PlannedItem[] = [];
    let bytes = 0;
    for (const it of plan) {
      bytes += it.size;
      const list = wanted.get(it.url);
      if (list) list.push(p.id);
      else wanted.set(it.url, [p.id]);
      if (!valid.has(it.url)) missing.push(it);
    }
    const status = nextPackStatus(p.status, missing.length);
    packs.set(p.id, { status, error: status === 'complete' ? null : p.status === 'error' ? p.error : null, bytes, missing });
  }

  for (const [url, it] of valid) {
    const ids = wanted.get(url);
    if (!ids || ids.length === 0) {
      deleteUrls.push(url);
      continue;
    }
    const sorted = [...ids].sort();
    const prev = [...it.packIds].sort();
    if (sorted.length !== prev.length || sorted.some((v, i) => v !== prev[i])) packIdUpdates.set(url, sorted);
  }

  const { recent, deleteUrls: recentDeleteUrls } = pruneRecent(input.recent, idx, wanted);
  return { deleteUrls, removePacks, packIdUpdates, packs, recent, recentDeleteUrls };
}

/** Every URL some pack plans. */
export function plannedUrls(plans: ReadonlyMap<string, readonly PlannedItem[] | null>): Set<string> {
  const out = new Set<string>();
  for (const plan of plans.values()) for (const it of plan ?? []) out.add(it.url);
  return out;
}

/**
 * The recent LRU keeps only URLs the manifest lists (same size) that no pack plans (one copy per
 * file). `changed` is false when nothing was dropped.
 */
export function pruneRecent(
  list: readonly RecentEntry[],
  idx: ReturnType<typeof manifestIndex>,
  wanted: { has(url: string): boolean },
): { recent: RecentEntry[]; deleteUrls: string[]; changed: boolean } {
  const deleteUrls: string[] = [];
  const recent: RecentEntry[] = [];
  for (const e of list) {
    const urls = e.urls.filter((u) => {
      const m = idx.get(u.url);
      const keep = !!m && m.size === u.size && !wanted.has(u.url);
      if (!keep) deleteUrls.push(u.url);
      return keep;
    });
    if (urls.length > 0) recent.push({ ...e, urls });
  }
  return { recent, deleteUrls, changed: deleteUrls.length > 0 };
}

/** Same-origin path of a Cache API key (absolute URL) — the form items / recent entries use. */
export function cachePath(url: string): string {
  try {
    const u = new URL(url, 'http://local');
    return u.pathname + u.search;
  } catch {
    return url;
  }
}

/** /api/media/<fileId>/<rev>/<part> → its parts (null for anything else). */
export function parseMediaUrl(url: string): { fileId: string; rev: number; part: string } | null {
  const m = /^\/api\/media\/([^/]+)\/(\d+)\/([^/?]+)$/.exec(url);
  return m ? { fileId: m[1]!, rev: Number(m[2]), part: m[3]! } : null;
}

/**
 * Reconciliation sweep of the content cache: every media copy the items table does not list and every
 * avatar the avatar list does not list is a stray (a put whose bookkeeping never landed, an
 * interrupted removal) and is deleted. `busy` (the file being stored right now) is left alone.
 */
export function contentStrays(
  keys: readonly string[],
  tracked: { items: { has(url: string): boolean }; avatars: { has(url: string): boolean } | null; busy: string | null },
): string[] {
  const out: string[] = [];
  for (const k of keys) {
    const path = cachePath(k);
    if (path === tracked.busy) continue;
    if (path.startsWith('/api/media/') ? !tracked.items.has(path) : path.startsWith('/api/avatar/') && tracked.avatars !== null && !tracked.avatars.has(path)) {
      out.push(k);
    }
  }
  return out;
}

/** Recent-cache keys the LRU list does not list. */
export function recentStrays(keys: readonly string[], list: readonly RecentEntry[]): string[] {
  const listed = new Set<string>();
  for (const e of list) for (const u of e.urls) listed.add(u.url);
  return keys.filter((k) => !listed.has(cachePath(k)));
}

/**
 * Stored URLs a 403/404 for `fileId` proves stale: a 403 (not visible) → every revision
 * (`upToRev` null); a 404 (that revision is not served) → that revision and older ones only.
 */
export function staleFileUrls(
  fileId: string,
  upToRev: number | null,
  items: Iterable<Pick<ItemRow, 'url' | 'fileId' | 'rev'>>,
  recent: readonly RecentEntry[],
): string[] {
  const out = new Set<string>();
  const stale = (rev: number) => upToRev === null || rev <= upToRev;
  for (const it of items) if (it.fileId === fileId && stale(it.rev)) out.add(it.url);
  for (const e of recent) {
    for (const u of e.urls) {
      const m = parseMediaUrl(u.url);
      if (m && m.fileId === fileId && stale(m.rev)) out.add(u.url);
    }
  }
  return [...out];
}

/** Sheets listed in the previous manifest but not in the new one (deleted, audience narrowed, section changed). */
export function removedSheetIds(prev: ManifestDTO | null, next: ManifestDTO): string[] {
  if (!prev || prev.userId !== next.userId) return [];
  const keep = new Set(next.files.map((f) => f.id));
  return prev.files.filter((f) => f.kind === 'sheet' && !keep.has(f.id)).map((f) => f.id);
}
